import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Menu, X, Download, Home, Route, Sparkles, Store, Tag, CircleHelp, Mail } from 'lucide-react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LanguageSelector } from './language-selector';
import logo from '@/assets/logos/coffeeback-logo-gpt.png';

type NavMenuProps = {
    isScrolled: boolean;
};

export function NavMenu({ isScrolled }: NavMenuProps) {
    const { t } = useTranslation();
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Navigation items in correct order
    const navItems = [
        { id: 'home', label: t('header.nav.home'), icon: Home },
        { id: 'how-it-works', label: t('header.nav.howItWorks'), icon: Route },
        { id: 'features', label: t('header.nav.features'), icon: Sparkles },
        { id: 'business-section', label: t('header.nav.forBusiness'), icon: Store },
        { id: 'pricing-section', label: t('header.nav.pricing'), icon: Tag },
        { id: 'faq-section', label: t('header.nav.faq'), icon: CircleHelp },
        { id: 'contact-section', label: t('header.nav.contact'), icon: Mail },
    ];

    // ─── Active Section Detection ───
    useEffect(() => {
        const sectionIds = navItems.map((item) => item.id);
        const observers: IntersectionObserver[] = [];

        sectionIds.forEach((id) => {
            const el = document.getElementById(id);
            if (!el) return;
            const observer = new IntersectionObserver(
                ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
                { rootMargin: '-40% 0px -55% 0px' }
            );
            observer.observe(el);
            observers.push(observer);
        });

        return () => observers.forEach((obs) => obs.disconnect());
    }, []);

    // ─── Scroll lock: use position:fixed on body instead of overflow:hidden ───
    // This avoids blocking pointer events on mobile
    useEffect(() => {
        if (isMobileOpen) {
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
        } else {
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        }
        return () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
        };
    }, [isMobileOpen]);

    const closeMobileMenu = useCallback(() => {
        setIsMobileOpen(false);
    }, []);

    const scrollToSection = useCallback((id: string) => {
        setIsMobileOpen(false);
        const scrollBehavior = () => {
            if (id === 'home') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
            }
        };

        if (location.pathname !== '/') {
            navigate('/');
            setTimeout(scrollBehavior, 150);
        } else {
            setTimeout(scrollBehavior, 150);
        }
    }, [location.pathname, navigate]);

    // ─── Framer Motion Variants ───
    const overlayVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.25 } },
        exit: { opacity: 0, transition: { duration: 0.2 } },
    };

    const menuContainerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.05, delayChildren: 0.1 },
        },
    };

    const menuItemVariants = {
        hidden: { opacity: 0, y: 16 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
        },
    };

    return (
        <>
            {/* ═══════════ Desktop Navigation ═══════════ */}
            <nav className="hidden xl:flex items-center gap-1">
                {navItems.map((item) => (
                    <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`relative px-3 py-2 text-sm font-medium transition-colors duration-300 rounded-md
                            hover:text-[#6F4E37] group
                            ${activeSection === item.id
                                ? 'text-[#6F4E37]'
                                : isScrolled ? 'text-foreground/80' : 'text-foreground/70'
                            }`}
                    >
                        {item.label}
                        <span
                            className={`absolute bottom-0.5 left-3 right-3 h-[2px] rounded-full bg-[#C8A27A] transition-all duration-300 origin-left
                                ${activeSection === item.id
                                    ? 'scale-x-100 opacity-100'
                                    : 'scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-60'
                                }`}
                        />
                    </button>
                ))}
            </nav>

            {/* ═══════════ Mobile Hamburger Button ═══════════ */}
            <button
                className="xl:hidden relative w-10 h-10 flex items-center justify-center rounded-full
                    hover:bg-foreground/5 transition-colors duration-200"
                onClick={() => setIsMobileOpen(true)}
                aria-label="Open menu"
            >
                <Menu className="h-5 w-5 text-foreground/80" />
            </button>

            {/* ═══════════ Mobile Full-Screen Overlay ═══════════ */}
            <AnimatePresence>
                {isMobileOpen && (
                    <motion.div
                        key="mobile-overlay"
                        variants={overlayVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="fixed inset-0 z-[60] xl:hidden flex flex-col overflow-hidden"
                        style={{
                            backdropFilter: 'blur(32px) saturate(220%) brightness(1.02)',
                            WebkitBackdropFilter: 'blur(32px) saturate(220%) brightness(1.02)',
                            backgroundColor: 'rgba(253, 248, 243, 0.72)',
                            borderBottom: '1px solid rgba(196, 167, 125, 0.12)',
                            boxShadow: 'inset 0 0 120px rgba(196, 167, 125, 0.06), 0 0 0 1px rgba(196, 167, 125, 0.08)',
                        }}
                    >
                        {/* Warm gradient tint layer — café gourmet depth */}
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background: 'linear-gradient(175deg, rgba(253, 248, 243, 0.3) 0%, rgba(196, 167, 125, 0.08) 40%, rgba(111, 78, 55, 0.04) 100%)',
                            }}
                        />
                        {/* ─── Header bar with close button ─── */}
                        <div className="flex items-center justify-between px-5 h-16 shrink-0">
                            <div className="flex items-center gap-2">
                                <img src={logo} alt="Coffeeback" className="h-9 w-auto" />
                                <span className="font-display font-bold text-lg italic text-foreground">
                                    Coffeeback
                                </span>
                            </div>
                            {/* Close button — native button, no Framer Motion wrapper */}
                            <button
                                ref={closeButtonRef}
                                type="button"
                                aria-label="Close menu"
                                style={{ zIndex: 9999, position: 'relative' }}
                                className="w-10 h-10 flex items-center justify-center rounded-full
                                    bg-transparent border-0
                                    hover:bg-foreground/10 active:bg-foreground/15 transition-colors"
                                onClick={() => setIsMobileOpen(false)}
                            >
                                <X size={20} color="currentColor" />
                            </button>
                        </div>

                        {/* ─── Navigation Links (animated) ─── */}
                        <motion.div
                            variants={menuContainerVariants}
                            initial="hidden"
                            animate="visible"
                            className="flex-1 flex flex-col items-start px-6 pt-6 pb-4 gap-2 overflow-y-auto"
                        >
                            {navItems.map((item) => (
                                <motion.button
                                    key={item.id}
                                    variants={menuItemVariants}
                                    onClick={() => scrollToSection(item.id)}
                                    className={`text-left px-5 py-3 text-lg font-medium rounded-full
                                        border backdrop-blur-sm transition-all duration-200 w-[220px]
                                        ${activeSection === item.id
                                            ? 'bg-background/70 border-[#C8A27A]/40 text-[#6F4E37] shadow-sm'
                                            : 'bg-background/50 border-border/30 text-foreground/80 hover:bg-background/70 hover:border-border/50'
                                        }`}
                                >
                                    <span className="flex items-center gap-3">
                                        <item.icon strokeWidth={2.5} className={`h-4 w-4 shrink-0 ${activeSection === item.id
                                            ? 'text-[#C8A27A]'
                                            : 'text-foreground/40'
                                            }`} />
                                        {item.label}
                                    </span>
                                </motion.button>
                            ))}
                        </motion.div>

                        {/* ─── Bottom: Selectors + CTAs ─── */}
                        <div className="px-6 pb-6 space-y-4 shrink-0">
                            {/* Language row */}
                            <div className="flex items-center justify-center py-3 border-t border-foreground/10">
                                <LanguageSelector />
                            </div>

                            {/* Auth Buttons */}
                            <div className="flex flex-col gap-3">
                                <Link to="/login" onClick={closeMobileMenu}>
                                    <Button
                                        className="w-full h-12 text-base font-medium rounded-xl
                                            bg-[#6F4E37] text-white hover:bg-[#5A3E2B]
                                            shadow-md hover:shadow-lg transition-all duration-300"
                                    >
                                        {t('header.cta.login')}
                                    </Button>
                                </Link>
                                <Link to="/register" onClick={closeMobileMenu}>
                                    <Button
                                        variant="outline"
                                        className="w-full h-12 text-base font-medium rounded-xl
                                            border-[#6F4E37]/25 text-[#6F4E37] hover:border-[#6F4E37]/50
                                            hover:bg-[#6F4E37]/5 transition-all duration-300"
                                    >
                                        {t('header.cta.register')}
                                    </Button>
                                </Link>
                            </div>

                            {/* Download — contextual link */}
                            <button
                                type="button"
                                className="flex items-center justify-center gap-2 w-full py-2.5
                                    text-sm text-[#6F4E37]/70 hover:text-[#6F4E37] transition-colors duration-300"
                            >
                                <Download className="h-3.5 w-3.5" />
                                {t('header.cta.download')}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
